import React from 'react';
import {
  List,
  ListItemButton,
  ListItemText,
  TextField,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Slider,
  Switch
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { SnackContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { config as layoutConfig } from 'src/components/pedigree-chart/pedigree-tool/pedigree/config';

const MENU_ITEMS = {
  TABLE_SETTINGS: 'tableSettings',
  PEDIGREE_SETTINGS: 'pedigreeSettings'
};

const CARD_SX = {
  bgcolor: '#fff',
  border: '0.5px solid',
  borderColor: 'divider',
  borderRadius: '12px',
  overflow: 'hidden'
};

const SECTION_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.disabled',
  mb: 1
};

const ROW_LABEL_W = 180;

const SLIDER_CAP_SX = {
  fontSize: 12,
  color: 'text.secondary',
  width: 60,
  flexShrink: 0
};

const ConfigRow = ({ label, isLast, children }) => (
  <Box
    sx={{
      px: 2.5,
      py: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      borderBottom: isLast ? 'none' : '0.5px solid',
      borderColor: 'divider',
      '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' }
    }}
  >
    <Box
      sx={{
        width: ROW_LABEL_W,
        flexShrink: 0,
        fontSize: 14,
        fontWeight: 500,
        color: 'text.primary'
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      {children}
    </Box>
  </Box>
);

const SaveRow = ({ onSave, saveState }) => {
  const t = useAutoTranslation();
  return (
    <Box
      sx={{
        px: 2.5,
        py: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: 'rgba(0,0,0,0.012)'
      }}
    >
      <Button
        variant="contained"
        color="secondary"
        size="small"
        onClick={onSave}
        disabled={saveState === 'saving'}
        endIcon={
          saveState === 'saving' ? (
            <CircularProgress size={12} color="inherit" />
          ) : null
        }
      >
        {t('Save changes')}
      </Button>
      {saveState === 'success' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 13,
            color: 'success.main'
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          {t('Saved')}
        </Box>
      )}
      {saveState === 'error' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 13,
            color: '#dc2626'
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
          {t('Save failed')}
        </Box>
      )}
    </Box>
  );
};

function TableSettingsPanel({ t, tableSettings, onPreferencesSaved }) {
  const { setSnack } = React.useContext(SnackContext);
  const [values, setValues] = React.useState(tableSettings);
  const [saveState, setSaveState] = React.useState('idle');

  React.useEffect(() => {
    setValues(tableSettings);
  }, [tableSettings]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: Number(value) }));
  };

  const onSave = async () => {
    setSaveState('saving');
    try {
      await axios.put(APICONSTANTS.updatePreferences, {
        tableSettings: values
      });
      localStorage.setItem('tablePreferenceDefaults', JSON.stringify(values));
      onPreferencesSaved(values);
      setSaveState('success');
      setTimeout(() => setSaveState('idle'), 3000);
    } catch {
      setSaveState('error');
      setSnack({
        status: true,
        msg: t('Failed to save table preferences.'),
        severity: 'error'
      });
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  return (
    <Box>
      <Box sx={SECTION_LABEL_SX}>{t('Table settings')}</Box>
      <Box sx={CARD_SX}>
        <ConfigRow label={t('Rows per page')}>
          <TextField
            name="rowsPerPage"
            value={values.rowsPerPage}
            onChange={onChange}
            size="small"
            select
            sx={{ width: 120 }}
          >
            {[5, 10, 15, 20, 25, 30, 50, 100].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </ConfigRow>
        <ConfigRow label={t('Default page index')}>
          <TextField
            name="pageIndex"
            type="number"
            value={values.pageIndex}
            onChange={onChange}
            size="small"
            sx={{ width: 120 }}
            slotProps={{
              htmlInput: { min: 0 }
            }}
          />
        </ConfigRow>
        <SaveRow onSave={onSave} saveState={saveState} />
      </Box>
    </Box>
  );
}

// The read-only "Clinical notes" panel that used to sit here is gone
// (TGCLOUD-1784). It showed two of the twelve drop-down lists, could not edit
// either, and was reached through the avatar menu rather than Settings. All
// twenty-four lists are now editable in Settings > Lookups by the three roles
// that are allowed to change them, which is where someone looking for them
// would go.

function PedigreeSettingsPanel({ t, pedigreeSettings, onPreferencesSaved }) {
  const { setSnack } = React.useContext(SnackContext);
  const [values, setValues] = React.useState(pedigreeSettings);
  const [saveState, setSaveState] = React.useState('idle');

  React.useEffect(() => {
    setValues(pedigreeSettings);
  }, [pedigreeSettings]);

  // Slider/toggle handlers — mirror the in-canvas Pedigree Settings dialog so
  // the defaults use the same controls, keys and ranges (symbol_size,
  // font_size, horizontal/vertical_spacing, auto_spacing).
  const autoOn = !!values.auto_spacing;
  const setPref = (name, value) =>
    setValues((prev) => ({ ...prev, [name]: value }));
  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setValues((prev) => ({ ...prev, [name]: checked }));
  };

  const onSave = async () => {
    setSaveState('saving');
    try {
      await axios.put(APICONSTANTS.updatePreferences, {
        pedigreeSettings: values
      });
      onPreferencesSaved(values);
      setSaveState('success');
      setTimeout(() => setSaveState('idle'), 3000);
    } catch {
      setSaveState('error');
      setSnack({
        status: true,
        msg: t('Failed to save pedigree preferences.'),
        severity: 'error'
      });
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  return (
    <Box>
      <Box sx={SECTION_LABEL_SX}>{t('Pedigree defaults')}</Box>
      <Box sx={CARD_SX}>
        <ConfigRow label={t('Symbol size')}>
          <Box sx={SLIDER_CAP_SX}>{t('Smaller')}</Box>
          <Slider
            size="small"
            value={Number(values.symbol_size) || 155}
            onChange={(_e, v) => setPref('symbol_size', v)}
            min={100}
            max={200}
            step={5}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>{t('Larger')}</Box>
        </ConfigRow>
        <ConfigRow label={t('Font size')}>
          <Box sx={SLIDER_CAP_SX}>{t('Smaller')}</Box>
          <Slider
            size="small"
            value={Number(values.font_size) || 70}
            onChange={(_e, v) => setPref('font_size', v)}
            min={30}
            max={130}
            step={5}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>{t('Larger')}</Box>
        </ConfigRow>
        {/* Separate axes, mirroring the in-canvas dialog (TGCLOUD-1610). */}
        <ConfigRow label={t('Horizontal spacing')}>
          <Box sx={SLIDER_CAP_SX}>{t('Tighter')}</Box>
          <Slider
            size="small"
            value={Number(values.horizontal_spacing) || 100}
            onChange={(_e, v) => setPref('horizontal_spacing', v)}
            min={100}
            max={500}
            step={50}
            disabled={autoOn}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>
            {t('Spacious')}
          </Box>
          {autoOn && (
            <Box
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                whiteSpace: 'nowrap'
              }}
            >
              {layoutConfig.ENABLE_PER_NODE_HORIZONTAL_SPACING
                ? t('auto · per node')
                : t('auto')}
            </Box>
          )}
        </ConfigRow>
        <ConfigRow label={t('Vertical spacing')}>
          <Box sx={SLIDER_CAP_SX}>{t('Tighter')}</Box>
          <Slider
            size="small"
            value={Number(values.vertical_spacing) || 100}
            onChange={(_e, v) => setPref('vertical_spacing', v)}
            min={100}
            max={500}
            step={50}
            disabled={autoOn}
            sx={{ flex: 1, mx: 1 }}
          />
          <Box sx={{ ...SLIDER_CAP_SX, textAlign: 'right' }}>
            {t('Spacious')}
          </Box>
          {autoOn && (
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('auto')}
            </Box>
          )}
        </ConfigRow>
        <ConfigRow label={t('Auto spacing')}>
          <Switch
            name="auto_spacing"
            checked={autoOn}
            onChange={handleToggle}
            size="small"
          />
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            {t('Derive spacing from label density & length')}
          </Box>
        </ConfigRow>
        <SaveRow onSave={onSave} saveState={saveState} />
      </Box>
    </Box>
  );
}

export default function Preferences() {
  const [activeMenu, setActiveMenu] = React.useState(MENU_ITEMS.TABLE_SETTINGS);
  const [preferences, setPreferences] = React.useState(
    CONSTANTS.initialContent.settings.preferences
  );
  const t = useAutoTranslation();

  React.useEffect(() => {
    axios
      .get(APICONSTANTS.getPreferences)
      .then(({ data }) => setPreferences(data))
      .catch(() => {});
  }, []);

  const onPreferencesSaved = (section, updated) => {
    setPreferences((prev) => ({ ...prev, [section]: updated }));
  };

  const menuItems = [
    { key: MENU_ITEMS.TABLE_SETTINGS, label: t('Table Settings') },
    { key: MENU_ITEMS.PEDIGREE_SETTINGS, label: t('Pedigree Settings') }
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: 400, gap: 3 }}>
      {/* Sidebar */}
      <List
        sx={{
          width: 160,
          flexShrink: 0,
          borderRight: '0.5px solid',
          borderColor: 'divider',
          pr: 1
        }}
      >
        {menuItems.map(({ key, label }) => (
          <ListItemButton
            key={key}
            selected={activeMenu === key}
            onClick={() => setActiveMenu(key)}
            sx={{
              borderRadius: '8px',
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'rgba(0,0,0,0.06)',
                fontWeight: 600
              },
              '&.Mui-selected:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
            }}
          >
            <ListItemText
              primary={label}
              slotProps={{
                primary: {
                  fontSize: 13,
                  fontWeight: activeMenu === key ? 600 : 400
                }
              }}
            />
          </ListItemButton>
        ))}
      </List>
      {/* Content */}
      <Box sx={{ flex: 1, pt: 0.5 }}>
        {activeMenu === MENU_ITEMS.TABLE_SETTINGS && (
          <TableSettingsPanel
            t={t}
            tableSettings={preferences.tableSettings}
            onPreferencesSaved={(updated) =>
              onPreferencesSaved('tableSettings', updated)
            }
          />
        )}
        {activeMenu === MENU_ITEMS.PEDIGREE_SETTINGS && (
          <PedigreeSettingsPanel
            t={t}
            pedigreeSettings={preferences.pedigreeSettings}
            onPreferencesSaved={(updated) =>
              onPreferencesSaved('pedigreeSettings', updated)
            }
          />
        )}
      </Box>
    </Box>
  );
}
